import React from 'react';
import injectSheet from 'react-jss';

import { compose, uniqueId } from 'shared/helpers';
import {BASE_COLOR, MOA_COLOR} from 'shared/constants/colors';
import {mixClasses as cx, mixStyles as sx} from 'helpers';

// All in px unless specified.
const INPUT_DEFAULT_FONT_SIZE = 16;
const INPUT_PADDING_TOP = 26;
const INPUT_PADDING_BOTTOM = 6;
const INPUT_UNDERLINE_HEIGHT = 2;
const HELPER_TEXT_DEFAULT_HEIGHT = '1rem';
const INTERACTION_TRANSITION = 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms';


const _inputLabelFont = {
  color: BASE_COLOR.moonGray,  // Should match placeholder text color
};

const _underlineBaseStyle = {
  position: 'absolute',
  content: '""',
  height: `${INPUT_UNDERLINE_HEIGHT}px`,
  width: 0,
  bottom: '0.5px',  // 0.5px is the JUST right pos!
  transition: INTERACTION_TRANSITION,
};

const _inputPlaceholderBaseStyle = {
  transition: INTERACTION_TRANSITION,
  color: BASE_COLOR.moonGray,
  fontWeight: 200,
};


/*
 * Input
 *
 * Reference:
 * http://tympanus.net/Development/TextInputEffects/
 * https://scotch.io/tutorials/google-material-design-input-boxes-in-css3
 */
class Input extends React.Component {
  // Flag to take props from Formation if used together.
  get canUseFormation() {
    return true;
  }

  constructor(props) {
    super(props);

    // So, dynamic styling of underline and associated animations need to be
    // isolated to only this input component. The tricky part is we are doing
    // underlines with css pseudo elements which is hard to style in line with
    // React. react-jss seems to be generating a same class for each component
    // definition so we'll isolate dynamic styling by namespacing with a unique
    // id. Hack? Maybe. Do I really care at this point? ¯\_(ツ)_/¯
    this.state = {
      uniqueId: uniqueId('inputText'),
      // hasPassedRegexReq: false,
    };
  }

  render() {
    const {
      classes: cn,
      label,
      placeholder,
      wrapClassName = 'mb3',
      wrapStyle = {},
      inputStyle = {},
      fontSize = INPUT_DEFAULT_FONT_SIZE,
      underlineDirection = 'left',  // left, center, right, none
      highlightColor = MOA_COLOR.pancake.puertoRico,
      shouldFloatLabel = false,
      helperText = null,
      helperTextAttr = {},
      errorText = null,
      errorTextAttr = {},
      ...props
    } = this.props;

    // Any arbitrary extra props onto input so callback funcs can access.
    const dataset = {
      'data-helper-text': helperText,
    };

    // Set default attributes for helperText and errorText.
    const _helperTextAttr = {
      ...helperTextAttr,
      // By default only show when in focus.
      shouldAlwaysShow: false,
    };
    const _errorTextAttr = {
      ...errorTextAttr,
      color: MOA_COLOR.emokid.mandy,
    };

    // Flag to control other style behaviors below when errored.
    const isErrorTextGiven = typeof errorText === 'string' && errorText.trim().length;

    // Style input dynamically.
    const inputSizeStyle = {
      fontSize,
      height: (
        INPUT_PADDING_TOP
        + INPUT_PADDING_BOTTOM
        + fontSize
        + 1  // border-bottom
      ),
    };
    const labelPos = {top: INPUT_PADDING_TOP - 1};
    const labelFontSize = {fontSize};

    const inputBorderStyleWhenErrored = isErrorTextGiven ? {
      borderBottomColor: _errorTextAttr.color
    } : {};

    // Style underline dynamically.
    const underlineClass = (
      underlineDirection === 'none' ? cn.underlineStatic : cn.underlineMotion
    );
    const _highlightColor = isErrorTextGiven ? _errorTextAttr.color : highlightColor;

    // Helper func for underline animation styles.
    const isLineDirection = (direction, trueVal, falseVal = '') => (
      underlineDirection === direction ? trueVal : falseVal
    );

    // See the note in constructor().
    const uniqueId = this.state.uniqueId;

    // Suppress floating animation if specified.
    const controlLabelFloating = shouldFloatLabel ? '' : `
      .${uniqueId} .moa-input ~ label,
    `;

    // Suppress placeholder text visibility if overlapping with label.
    const isLabelGiven = typeof label === 'string' && label.trim().length;
    const isLabelInTheWay = isLabelGiven && shouldFloatLabel;
    const controlPlaceholderVisibility = isLabelInTheWay ? `
      .${uniqueId} .moa-input::-webkit-input-placeholder  { opacity: 0; }
      .${uniqueId} .moa-input::-moz-placeholder           { opacity: 0; }
      .${uniqueId} .moa-input:-ms-input-placeholder       { opacity: 0; }
    ` : '';

    // By default only show helper text when on focus.
    let controlHelperTextVisibility = `
      .${uniqueId} .${cn.helperText} {
        max-height: ${HELPER_TEXT_DEFAULT_HEIGHT};
        opacity: 0;
      }
      .${uniqueId} .${cn.input}:focus ~ .${cn.helperText} {
        max-height: none;
        opacity: 1;
      }
    `;
    if (_helperTextAttr.shouldAlwaysShow || isErrorTextGiven) {
      controlHelperTextVisibility = '';
    }

    // Set default helper text color, unless error text is given.
    let helperTextColor = BASE_COLOR.lightSilver;
    if (isErrorTextGiven) {
      helperTextColor = _errorTextAttr.color;
    }
    const controlHelperTextColor = `
      .${uniqueId} .${cn.helperText} {
        color: ${helperTextColor};
      }
    `;

    // Highlight label with error color if given
    const overrideLabelColorStyleWhenErrored = isErrorTextGiven ? `
      color: ${_errorTextAttr.color} !important;
    ` : '';

    return (
      <div
        style={wrapStyle}
        className={cx('relative', wrapClassName, uniqueId)}
      >
        {/* Dynamically override underline behavior based on underlineDirection prop. */}
        <style type="text/css" dangerouslySetInnerHTML={{__html: `
            /* Control underline animation direction dynamically. */
            .${uniqueId} .${cn.underlineMotion}:before {${isLineDirection('right', 'left: 0;')}}
            .${uniqueId} .${cn.underlineMotion}:after  {${isLineDirection('left', 'right: 0;')}}
            .${uniqueId} .${cn.input}:focus ~ .${cn.underlineMotion}:before {
              ${isLineDirection('left', 'width: 0;', 'width: 50%;')}
              ${isLineDirection('right', 'width: 100%;')}
            }
            .${uniqueId} .${cn.input}:focus ~ .${cn.underlineMotion}:after {
              ${isLineDirection('left', 'width: 100%;', 'width: 50%;')}
              ${isLineDirection('right', 'width: 0;')}
            }

            /* Style static underline. */
            .${uniqueId} .${cn.input} ~ .${cn.underlineStatic} {
              top: ${INPUT_PADDING_TOP + fontSize + INPUT_PADDING_BOTTOM}px;
            }
            .${uniqueId} .${cn.input}:focus ~ .${cn.underlineStatic} {
              height: ${INPUT_UNDERLINE_HEIGHT}px;
              background: ${_highlightColor};
            }

            /* Control underline color. */
            .${uniqueId} .${cn.underlineMotion}:before,
            .${uniqueId} .${cn.underlineMotion}:after {
              background: ${_highlightColor};
            }


            /* Style label dynamically */
            .${uniqueId} .moa-input:focus ~ label {
              color: ${_highlightColor} !important;
            }
            .${uniqueId} .moa-input:valid:not(:focus) ~ label {
              color: ${_highlightColor} !important;
            }
            /* 'controlLabelFloating' must be positioned first. */
            ${controlLabelFloating}
            .${uniqueId} .moa-input:focus ~ label,
            .${uniqueId} .moa-input:valid ~ label {
              top: 0 !important;
              font-size: ${fontSize * 0.8}px !important;
              ${overrideLabelColorStyleWhenErrored}
            }

            /* Control placeholder visibility, avoid overlapping with label. */
            ${controlPlaceholderVisibility}

            /* Control helper text visibility & style. */
            ${controlHelperTextVisibility}
            ${controlHelperTextColor}
          `,
        }} />

        <input
          required
          type="text"
          className={cx(cn.input, 'moa-input relative dib w-100 bg-transparent')}
          style={sx(inputStyle, inputSizeStyle, inputBorderStyleWhenErrored)}
          placeholder={placeholder}
          {...dataset}
          {...props}
        />
        <label
          className={cx(cn.label, 'absolute')}
          style={sx(labelPos, labelFontSize)}
        >
          {label}
        </label>
        <span className={cx(underlineClass, 'relative db w-100')} />
        <div className={cx(cn.helperText, 'mv1 lh-title')}>
          {isErrorTextGiven ? errorText : helperText}
        </div>
      </div>
    );
  }
}

const styles = {
  input: {
    color: BASE_COLOR.nearBlack,
    transition: INTERACTION_TRANSITION,
    // http://react-md.mlaursen.com/components/text-fields
    padding: `${INPUT_PADDING_TOP}px 1px ${INPUT_PADDING_BOTTOM}px`,
    borderRadius: 0,
    border: 'none',
    borderBottom: '1px solid #E6E6E6',
    cursor: 'text',
    lineHeight: 1.5,
    '&:hover': {
      borderColor: BASE_COLOR.moonGray,
    },
    '&:focus': {
      color: BASE_COLOR.nearBlack,
      outline: 'none',
      boxShadow: 'none',
      borderColor: 'transparent',
      opacity: 1,
    },

    // Style placeholder text.. (https://css-tricks.com/almanac/selectors/p/placeholder/)
    '&::-webkit-input-placeholder': {..._inputPlaceholderBaseStyle},
    '&::-moz-placeholder':          {..._inputPlaceholderBaseStyle},
    '&:-ms-input-placeholder':      {..._inputPlaceholderBaseStyle},
    // Make sure they are visible when in focus.
    '&:focus::-webkit-input-placeholder': {opacity: '1 !important'},
    '&:focus::-moz-placeholder':          {opacity: '1 !important'},
    '&:focus:-ms-input-placeholder':      {opacity: '1 !important'},
  },
  label: {
    zIndex: 1,
    padding: '0 1px',
    top: 0,
    left: 0,
    transition: INTERACTION_TRANSITION,
    pointerEvents: 'none',
    ..._inputLabelFont,
  },
  underlineMotion: {
    '&:before': {..._underlineBaseStyle, left: '50%'},
    '&:after': {..._underlineBaseStyle, right: '50%'},
  },
  underlineStatic: {
    ..._underlineBaseStyle,
    width: '100%',
    height: 0,
  },
  helperText: {
    fontSize: '12px',
    fontWeight: 200,
    transition: INTERACTION_TRANSITION,
    minHeight: HELPER_TEXT_DEFAULT_HEIGHT,
  },
};


export default compose(
  injectSheet(styles)
)(Input);



// WEBPACK FOOTER //
// ./src/components/elems/Input.js